import { supabase } from '../../config/supabase';

export const triggerPushForNotification = async (notificationId) => {
    if (!notificationId) {
        return { skipped: true, reason: 'missing_notification_id' };
    }

    try {
        const { data, error } = await supabase.functions.invoke('send-push-notification', {
            body: {
                notification_id: notificationId,
            }
        });

        if (error) {
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error triggering push notification:', error);
        return {
            success: false,
            error: error.message || 'Unknown push trigger error'
        };
    }
};

export const createNotificationWithFallback = async (notificationData, fallbackFn) => {
    let notification = null;

    try {
        notification = await createNotificationWithoutPush(notificationData);

        const pushResult = await triggerPushForNotification(notification.id);

        const pushFailed =
            !pushResult ||
            pushResult.success === false ||
            pushResult.error ||
            pushResult.skipped ||
            pushResult.reason === 'no_tokens' ||
            pushResult.reason === 'missing_push_token' ||
            pushResult.sent === 0;

        if (pushFailed && typeof fallbackFn === 'function') {
            await fallbackFn({
                notification,
                pushResult
            });

            return {
                notification,
                pushResult,
                fallbackUsed: true
            };
        }

        return {
            notification,
            pushResult,
            fallbackUsed: false
        };
    } catch (error) {
        console.error('Notification with fallback failed:', error);

        if (typeof fallbackFn === 'function') {
            await fallbackFn({
                notification,
                error
            });

            return {
                notification,
                pushResult: null,
                fallbackUsed: true,
                error
            };
        }

        throw error;
    }
};

export const createNotificationWithoutPush = async (notificationData) => {
    const { data: notification, error } = await supabase
        .from('notifications')
        .insert({
            user_id: notificationData.user_id,
            company_id: notificationData.company_id,
            type: notificationData.type,
            title: notificationData.title,
            message: notificationData.message,
            priority: notificationData.priority || 'normal',
            related_type: notificationData.related_type || null,
            related_id: notificationData.related_id || null,
            action_url: notificationData.action_url || null,
            metadata: notificationData.metadata || null
        })
        .select()
        .single();

    if (error) throw error;

    return notification;
};

export const createNotification = async (notificationData) => {
    const notification = await createNotificationWithoutPush(notificationData);
    await triggerPushForNotification(notification.id);
    return notification;
};

/**
 * Get notifications for a user
 */
export const getNotifications = async (userId, showRead = false, limit = 50) => {
    try {
        let query = supabase
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (!showRead) {
            query = query.eq('is_read', false);
        }

        const { data: notifications, error } = await query;

        if (error) throw error;

        return notifications || [];
    } catch (error) {
        console.error('Error fetching notifications:', error);
        throw error;
    }
};

/**
 * Get unread notification count
 */
export const getUnreadNotificationCount = async (userId) => {
    try {
        const { count, error } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return count || 0;
    } catch (error) {
        console.error('Error fetching unread notification count:', error);
        return 0;
    }
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (notificationId) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('id', notificationId);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error marking notification as read:', error);
        throw error;
    }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userId) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error marking all notifications as read:', error);
        throw error;
    }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', notificationId);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error deleting notification:', error);
        throw error;
    }
};

/**
 * Create a notification
 */
// export const createNotification = async (notificationData) => {

//     try {
//         const { data: notification, error } = await supabase
//             .from('notifications')
//             .insert({
//                 user_id: notificationData.user_id,
//                 company_id: notificationData.company_id,
//                 type: notificationData.type,
//                 title: notificationData.title,
//                 message: notificationData.message,
//                 priority: notificationData.priority || 'normal',
//                 related_type: notificationData.related_type || null,
//                 related_id: notificationData.related_id || null,
//                 action_url: notificationData.action_url || null,
//                 metadata: notificationData.metadata || null
//             })
//             .select()
//             .single();

//         if (error) throw error;

//         await triggerPushForNotification(notification.id);

//         return notification;
//     } catch (error) {
//         console.error('Error creating notification:', error);
//         throw error;
//     }
// };

export const sendEstimationCompletedNotifications = async ({
    jobId,
    companyId,
    companySlug,
    completedById,
    completedByName,
    estimatorId,
    estimatorName,
    completionNote,
    uploadedFileIds = [],
}) => {
    try {
        const { error: notifyError } = await supabase.functions.invoke(
            "send-estimation-completed-notification",
            {
                body: {
                    job_id: jobId,
                    company_id: companyId,
                    company_slug: companySlug,
                    completed_by_id: completedById,
                    completed_by_name: completedByName,
                    estimator_id: estimatorId,
                    estimator_name: estimatorName,
                    completion_note: completionNote || null,
                    uploaded_file_ids: uploadedFileIds,
                },
            }
        );

        if (notifyError) throw notifyError;
    } catch (error) {
        console.error("Error sending estimation completed notifications:", error);
    }
};

/**
 * Subscribe to new notifications
 */
export const subscribeToNotifications = (userId, callback) => {
    const subscription = supabase
        .channel('notifications')
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `user_id=eq.${userId}`
            },
            (payload) => {
                callback(payload.new);
            }
        )
        .subscribe();

    return subscription;
};

/**
 * Notification Types
 */
export const NOTIFICATION_TYPES = {
    ASSIGNMENT_CREATED: 'assignment_created',
    ASSIGNMENT_UPDATED: 'assignment_updated',
    ASSIGNMENT_COMPLETED: 'assignment_completed',
    ASSIGNMENT_DUE_SOON: 'assignment_due_soon',
    ASSIGNMENT_ASSIGNED: 'assignment_assigned',
    MESSAGE_RECEIVED: 'message_received',
    USER_MENTIONED: 'user_mentioned',
    SYSTEM_ALERT: 'system_alert',
    SYSTEM_UPDATE: 'system_update',
    PAYMENT_SUCCESS: 'payment_success',
    PAYMENT_FAILED: 'payment_failed',
    PRICING_ENTERED: 'pricing_entered',
    READY_FOR_QUOTATION: 'ready_for_quotation'
};

/**
 * Notification Priorities
 */
export const NOTIFICATION_PRIORITIES = {
    LOW: 'low',
    NORMAL: 'normal',
    HIGH: 'high',
    URGENT: 'urgent'
};
